<template>
    <div class="books-list">
        <SearchInput
            v-if="books.length || searchString"
            :placeholder="$t('booksList.searchPlaceholder')"
            class="books-list__search"
            @change="onSearch"
        />
        <Spinner v-if="loading" class="spinner--transparent" />
        <template v-if="books.length">
            <transition-group name="fade" appear>
                <BookCard
                    v-for="book in books"
                    :key="book.id"
                    :book="book"
                    @deleted="onBookDeleted"
                />
            </transition-group>
            <button
                v-if="canLoadMore"
                type="button"
                class="books-list__loadmore"
                :disabled="loading"
                @click.prevent="nextPage"
            >
                {{ $t('misc.loadmore') }}
            </button>
        </template>
        <template v-if="!loading && !searchString && !books.length">
            <p style="color: #161838; font-size: 18px; text-align: center;">
                {{ $t('booksList.nobooks') }}
            </p>
            <p v-if="!isListener" style="color: #161838; text-align: center;">
                <router-link to="/faq" class="link">
                    {{ $t('booksList.faq') }}
                </router-link>
            </p>
            <p v-if="!isListener" style="text-align: center;">
                <iframe
                    width="560"
                    height="315"
                    :src="youtubeSrc"
                    frameborder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                />
            </p>
            <p v-if="!isListener" style="color: #161838; text-align: center;">
                <router-link to="/docs/getting-started" class="link">
                    {{ $t('booksList.tutorial') }}
                </router-link>
            </p>
        </template>
        <template v-if="searchString && !loading && !books.length">
            <p style="color: #161838;">{{ $t('booksList.notfound') }}</p>
        </template>
        <InteractiveGuide
            v-if="!$store.state.auth.isAdmin && !$store.state.auth.isListener"
            ref="tutorial"
            name="noobooks"
            :steps="tutorialSteps[$i18n.locale]"
        />
    </div>
</template>

<script>
import BookCard from '../BookCard/BookCard';
import InteractiveGuide from '@speechki-frontend/shared/ui/InteractiveGuide';
import { SearchInput } from '@speechki-frontend/shared/ui/forms';

const NO_BOOKS_TUTORIAL_STEPS = {
    eng: [
        {
            target: '[data-v-step="1"]',
            title: 'Book uploading',
            content: 'To get started just press this button.',
            params: {
                placement: 'bottom'
            },
            offset: -100
        },
        {
            target: '[data-v-step="1"]',
            title: 'Book uploading',
            content: 'You can use any DOCX text to try.',
            params: {
                placement: 'bottom'
            },
            offset: -100
        }
    ],
    rus: [
        {
            target: '[data-v-step="1"]',
            title: 'Загрузка книги',
            content: 'Загрузите текст вашей книги. Для этого нажмите на кнопку.',
            params: {
                placement: 'bottom'
            },
            offset: -100
        },
        {
            target: '[data-v-step="1"]',
            title: 'Загрузка книги',
            content: 'Вы можете использовать любой текст для тестирования функций сервиса.',
            params: {
                placement: 'bottom'
            },
            offset: -100
        }
    ]
};

const tutorialSources = {
    rus: 'https://www.youtube.com/embed/00yMFvByHW0',
    eng: 'https://www.youtube.com/embed/z3isl6ViqTA'
};

export default {
    name: 'BooksList',
    components: {
        BookCard,
        InteractiveGuide,
        SearchInput
    },
    data() {
        return {
            books: [],
            booksCount: 0,
            page: 1,
            pageSize: 10,
            loading: false,
            searchString: '',
            searchInputTimeout: null,
            tutorialSteps: NO_BOOKS_TUTORIAL_STEPS
        };
    },
    computed: {
        canLoadMore() {
            return this.page * this.pageSize < this.booksCount;
        },
        isListener() {
            return this.$store.getters.isListener;
        },
        youtubeSrc() {
            return tutorialSources[this.$i18n.locale];
        }
    },
    watch: {
        page() {
            this.fetchBooks();
        },
        searchString(newv, oldv) {
            this.page = 1;

            if (!newv && oldv) {
                this.fetchBooks();
            }
        }
    },
    created() {
        this.fetchBooks();
    },
    methods: {
        async fetchBooks() {
            try {
                this.loading = true;

                const params = {
                    page: this.page,
                    page_size: this.pageSize,
                    ordering: '-id'
                };

                if (this.searchString) {
                    params.search = this.searchString;
                }

                const { results, count } = await this.$http.get('/api/v2/books/', {
                    params
                });

                if (this.page === 1) {
                    this.books = results;
                    this.booksCount = count;

                    if (!results.length) {
                        this.$refs.tutorial.checkAndRun();
                    }
                } else {
                    this.books = [...this.books, ...results];
                }
                this.loading = false;
            } catch (error) {
                this.loading = false;
                console.log(error);
            }
        },
        nextPage() {
            this.page++;
        },
        onBookDeleted(id) {
            this.books = this.books.filter(book => book.id !== id);
        },
        onSearch(searchString) {
            this.searchString = searchString;
            this.fetchBooks();
        }
    }
};
</script>

<style lang="scss" src="./BooksList.scss"></style>
